import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    ArrowLeft, RefreshCw, Pencil, Sparkles, BarChart3, Wand2,
    Loader2, AlertTriangle, Zap, LogOut, ChevronDown, X, Check,
} from "lucide-react";
import Background from "@/components/landing/Background";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";
import LaunchWizard from "@/pages/onboarding/LaunchWizard";
import PreflightCard from "@/components/PreflightCard";
import { StrategyView } from "@/pages/onboarding/Step3Strategy";
import LimitGate from "@/components/LimitGate";
import AppShell from "@/components/nav/AppShell";
import { refreshUsage } from "@/hooks/useUsage";
import { UpgradeModal } from "@/components/UsageWidget";

const LOADING_TEXTS = [
    "Аналізуємо ваш бізнес...",
    "Перебудовуємо структуру кампанії...",
    "Підбираємо нові ключові слова...",
    "Генеруємо варіації та опечатки...",
    "Пишемо тексти оголошень...",
    "Перевіряємо валідність...",
    "Майже готово...",
];

/* ─────────────────── Page ─────────────────── */
export default function Strategy() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [strategy, setStrategy] = useState(null);
    const [bizInfo, setBizInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [genStage, setGenStage] = useState(0);
    const [tab, setTab] = useState("strategy"); // strategy | wizard
    // PR #8: ?tab=wizard&campaign=N з URL (deep-link з Health Check)
    const urlCampaignIdx = (() => {
        const c = searchParams.get("campaign");
        const n = c != null ? parseInt(c, 10) : NaN;
        return Number.isFinite(n) && n >= 0 ? n : null;
    })();
    useEffect(() => {
        if (searchParams.get("tab") === "wizard") {
            setTab("wizard");
        }
    }, [searchParams]);
    const [confirmModal, setConfirmModal] = useState(null); // 'regenerate' | 'new' | null
    const [menuOpen, setMenuOpen] = useState(false);
    const [error, setError] = useState("");
    const [limitErr, setLimitErr] = useState(null); // 402 from backend → opens UpgradeModal

    const loadAll = async () => {
        try {
            const [s, b] = await Promise.all([api.get("/strategy"), api.get("/business")]);
            setStrategy(s.data?.strategy_json ? s.data : null);
            setBizInfo(b.data || null);
        } catch {}
        setLoading(false);
    };
    useEffect(() => { loadAll(); }, []);

    // Cycle through loader stages
    useEffect(() => {
        if (!generating) return;
        const t = setInterval(() => setGenStage((s) => (s + 1) % LOADING_TEXTS.length), 6000);
        return () => clearInterval(t);
    }, [generating]);

    // Auto-trigger regeneration when user returns from Step2 edit (?regenerate=1)
    useEffect(() => {
        if (loading) return;
        if (searchParams.get("regenerate") === "1" && !generating) {
            // Clear flag immediately so refresh doesn't loop
            searchParams.delete("regenerate");
            setSearchParams(searchParams, { replace: true });
            regenerate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    const regenerate = async () => {
        setConfirmModal(null);
        setError("");
        setGenerating(true);
        setGenStage(0);
        try {
            await api.delete("/strategy");
            // POST /strategy/generate may return 402 if user is out of regenerations.
            await api.post("/strategy/generate");
            await refreshUsage(); // counter was consumed server-side
            // Poll
            for (let i = 0; i < 80; i++) {
                await new Promise((r) => setTimeout(r, 3000));
                const r = await api.get("/strategy");
                if (r.data?.status === "ready" && r.data?.strategy_json) {
                    setStrategy(r.data);
                    setGenerating(false);
                    return;
                }
                if (r.data?.status === "error") {
                    throw new Error(r.data?.error || "Помилка генерації");
                }
            }
            throw new Error("Перевищено таймаут — спробуйте пізніше");
        } catch (e) {
            const detail = e?.response?.data?.detail;
            // 402 with limit_exceeded structure → show UpgradeModal directly
            if (detail && typeof detail === "object" && detail.error === "limit_exceeded") {
                setLimitErr(detail);
                setGenerating(false);
                return;
            }
            setError(typeof detail === "string" ? detail : (detail?.message || e?.message || "Не вдалося перегенерувати"));
            setGenerating(false);
        }
    };

    const startFromScratch = async () => {
        setConfirmModal(null);
        // Wipe strategy AND business → user re-fills questionnaire
        try {
            await api.delete("/strategy");
            // Note: we do NOT delete business — user can edit it on step-2.
            // If they want a fully fresh persona, they can clear fields manually.
            navigate("/onboarding/step-2?from=strategy");
        } catch (e) {
            setError(e?.response?.data?.detail || "Не вдалося очистити стратегію");
        }
    };

    return (
        <AppShell testid="strategy-page">
                    {/* Title + actions */}
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <h1 className="font-display font-extrabold text-text-primary" style={{ fontSize: "clamp(28px, 4vw, 44px)", letterSpacing: "-0.02em" }}>
                                Стратегія та кампанії
                            </h1>
                            <p className="mt-2 text-secondary-c max-w-[680px]">
                                Перегляньте згенеровану стратегію, скопіюйте ключові слова, або оновіть дані бізнесу і перегенеруйте стратегію з оновленою інформацією.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2 shrink-0">
                            <button
                                onClick={() => navigate("/onboarding/step-2?from=strategy")}
                                className="btn-ghost"
                                data-testid="action-edit-business"
                                disabled={generating}
                            >
                                <Pencil size={14} /> Редагувати дані
                            </button>
                            <LimitGate
                                limitKey="regenerations"
                                testid="action-regenerate-gate"
                                reason="Ви досягли ліміту перегенерацій стратегії на цей місяць. Перейдіть на вищий тариф або зачекайте до скидання."
                            >
                                <button
                                    onClick={() => setConfirmModal("regenerate")}
                                    className="btn-ghost"
                                    data-testid="action-regenerate"
                                    disabled={generating || !strategy}
                                >
                                    <RefreshCw size={14} /> Перегенерувати
                                </button>
                            </LimitGate>
                            <LimitGate
                                limitKey="regenerations"
                                testid="action-new-strategy-gate"
                                reason="Створення нової стратегії використовує ту саму квоту перегенерацій. Перейдіть на вищий тариф або зачекайте."
                            >
                                <button
                                    onClick={() => setConfirmModal("new")}
                                    className="btn-ghost"
                                    data-testid="action-new-strategy"
                                    disabled={generating}
                                >
                                    <Sparkles size={14} /> Нова з нуля
                                </button>
                            </LimitGate>
                        </div>
                    </div>

                    {error && (
                        <div className="mt-4 p-3 rounded-lg border border-error/30 bg-error/8 text-sm text-error-c font-mono" data-testid="strategy-error">
                            {error}
                        </div>
                    )}

                    {/* Loading state */}
                    {(loading || generating) && (
                        <section className="mt-8 glass rounded-2xl p-10 text-center" data-testid="strategy-loading">
                            <div className="inline-flex items-center gap-3 text-primary-c">
                                <Loader2 size={20} className="animate-spin" />
                                <span className="font-mono text-sm">
                                    {generating ? LOADING_TEXTS[genStage] : "Завантажуємо стратегію..."}
                                </span>
                            </div>
                        </section>
                    )}

                    {/* Empty state — no strategy yet */}
                    {!loading && !generating && !strategy && (
                        <section className="mt-8 glass rounded-2xl p-10 text-center" data-testid="strategy-empty">
                            <AlertTriangle size={32} className="text-warning-c mx-auto" />
                            <h2 className="mt-4 font-display font-bold text-xl text-text-primary">Стратегію ще не згенеровано</h2>
                            <p className="mt-2 text-secondary-c">Заповніть онбординг, щоб AI створив структуру кампанії.</p>
                            <button onClick={() => navigate("/onboarding")} className="mt-6 btn-primary pulse-glow" data-testid="strategy-empty-cta">
                                Перейти до онбордингу
                            </button>
                        </section>
                    )}

                    {/* Strategy + Wizard */}
                    {!loading && !generating && strategy?.strategy_json && (
                        <>
                            {/* Multi-campaign launch summary — show only when >1 campaign */}
                            {((strategy.strategy_json.campaigns || []).filter(c => (c.type || "Search").toLowerCase() === "search").length > 1) && (
                                <MultiCampaignSummary
                                    campaigns={strategy.strategy_json.campaigns}
                                    launchedIndexes={strategy.launched_indexes || (strategy.launched ? [0] : [])}
                                    onGoToWizard={(idx) => { setTab("wizard"); /* wizard will pick its own state */ window.scrollTo({ top: 0, behavior: "smooth" }); }}
                                />
                            )}

                            {/* Tabs */}
                            <div className="mt-7 sticky top-0 z-20 -mx-4 px-4 py-3 backdrop-blur-md bg-background/80 border-b border-border">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <TabButton
                                        active={tab === "strategy"}
                                        onClick={() => setTab("strategy")}
                                        icon={<BarChart3 size={15} />}
                                        label="Стратегія"
                                        testid="strategy-tab-view"
                                    />
                                    <TabButton
                                        active={tab === "wizard"}
                                        onClick={() => setTab("wizard")}
                                        icon={<Wand2 size={15} />}
                                        label="Запуск кампанії"
                                        testid="strategy-tab-wizard"
                                    />
                                </div>
                            </div>

                            {tab === "strategy" && (
                                <StrategyView
                                    budget={strategy.strategy_json.budget || strategy.strategy_json.budget_analysis || {}}
                                    campaigns={strategy.strategy_json.campaigns || (strategy.strategy_json.campaign ? [strategy.strategy_json.campaign] : [])}
                                    warnings={strategy.strategy_json.warnings || []}
                                    recommendations={strategy.strategy_json.additional_recommendations || []}
                                    onGoLaunch={() => { setTab("wizard"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                                />
                            )}

                            {tab === "wizard" && (
                                <div className="mt-6 space-y-6">
                                    <PreflightCard />
                                    <LaunchWizard
                                    userId={user?.user_id}
                                    strategy={strategy.strategy_json}
                                    bizInfo={bizInfo}
                                    initialCampaignIdx={urlCampaignIdx}
                                    onSkip={() => navigate("/dashboard")}
                                    onFinish={() => navigate("/dashboard")}
                                />
                                </div>
                            )}
                        </>
                    )}

            {/* Confirm modals */}
            {confirmModal === "regenerate" && (
                <ConfirmModal
                    title="Перегенерувати стратегію?"
                    body="Поточна стратегія буде замінена новою (з тими ж даними бізнесу). Прогрес у Wizard'і зберігається."
                    confirmLabel="Так, перегенерувати"
                    onConfirm={regenerate}
                    onCancel={() => setConfirmModal(null)}
                    testidPrefix="confirm-regen"
                />
            )}
            {confirmModal === "new" && (
                <ConfirmModal
                    title="Створити нову стратегію з нуля?"
                    body="Поточну стратегію буде видалено, і вас перенаправить на сторінку анкети. Заповніть її заново або скоригуйте відповіді — і AI створить нову стратегію."
                    confirmLabel="Так, з нуля"
                    onConfirm={startFromScratch}
                    onCancel={() => setConfirmModal(null)}
                    testidPrefix="confirm-new"
                />
            )}

            {limitErr && (
                <UpgradeModal
                    currentPlan={limitErr.current_plan}
                    recommendedPlan={limitErr.recommended_plan}
                    reason={limitErr.message}
                    onClose={() => setLimitErr(null)}
                />
            )}
        </AppShell>
    );
}

const MultiCampaignSummary = ({ campaigns = [], launchedIndexes = [], onGoToWizard }) => {
    const searchCampaigns = campaigns.filter(c => (c.type || "Search").toLowerCase() === "search");
    const launchedSet = new Set(launchedIndexes);
    const launchedCount = searchCampaigns.filter((_, i) => launchedSet.has(i)).length;
    const pct = Math.round((launchedCount / searchCampaigns.length) * 100);

    return (
        <section className="mt-7 glass rounded-2xl p-6" data-testid="multi-campaign-summary">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h3 className="font-display font-bold text-base text-text-primary">
                        Прогрес запуску кампаній
                    </h3>
                    <p className="mt-1 text-xs text-secondary-c">
                        У вашій стратегії <span className="text-text-primary font-mono">{searchCampaigns.length}</span> кампаній. Кожну потрібно пройти через Wizard окремо.
                    </p>
                </div>
                <div className="text-right">
                    <div className="font-display font-bold text-2xl tabular-nums text-success-c">
                        {launchedCount}<span className="text-secondary-c text-base">/{searchCampaigns.length}</span>
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-faint-c">запущено</div>
                </div>
            </div>

            <div className="mt-4 h-2 bg-surface rounded-full overflow-hidden border border-border">
                <div className="h-full bg-success transition-[width] duration-700 ease-out" style={{ width: `${pct}%` }} />
            </div>

            <div className="mt-4 grid gap-2">
                {searchCampaigns.map((c, i) => {
                    const launched = launchedSet.has(i);
                    return (
                        <div
                            key={i}
                            className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${launched ? "border-success/30 bg-success/5" : "border-border bg-surface-hover/30"}`}
                            data-testid={`campaign-summary-row-${i}`}
                        >
                            <div className="flex items-center gap-3 min-w-0">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-mono font-bold shrink-0 ${launched ? "bg-success text-background" : "border border-border text-secondary-c"}`}>
                                    {launched ? <Check size={12} /> : i + 1}
                                </div>
                                <div className="min-w-0">
                                    <div className="text-sm text-text-primary truncate">{c.name}</div>
                                    <div className="text-[11px] font-mono text-faint-c">
                                        {(c.ad_groups || []).length} груп · {(c.ad_groups || []).reduce((a, g) => a + (g.keywords || []).length, 0)} ключів
                                    </div>
                                </div>
                            </div>
                            {launched ? (
                                <span className="text-[11px] font-mono text-success-c shrink-0">✓ запущено</span>
                            ) : (
                                <button
                                    onClick={() => onGoToWizard(i)}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-primary/40 bg-primary/10 text-primary-c text-[11px] font-mono hover:bg-primary/20 transition shrink-0"
                                    data-testid={`campaign-summary-launch-${i}`}
                                >
                                    Запустити →
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

const TabButton = ({ active, onClick, icon, label, testid }) => (
    <button
        onClick={onClick}
        data-testid={testid}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
            active
                ? "bg-primary text-white"
                : "border border-border text-secondary-c hover:text-text-primary hover:border-primary/40"
        }`}
    >
        {icon} {label}
    </button>
);

const ConfirmModal = ({ title, body, confirmLabel, onConfirm, onCancel, testidPrefix }) => (
    <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
        onClick={onCancel}
        data-testid={`${testidPrefix}-modal`}
    >
        <div
            className="glass rounded-2xl p-6 max-w-[480px] w-full"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="flex items-start justify-between gap-3">
                <h3 className="font-display font-bold text-lg text-text-primary">{title}</h3>
                <button
                    onClick={onCancel}
                    className="text-secondary-c hover:text-text-primary"
                    data-testid={`${testidPrefix}-close`}
                >
                    <X size={18} />
                </button>
            </div>
            <p className="mt-3 text-sm text-text-primary/85 leading-relaxed">{body}</p>
            <div className="mt-6 flex items-center justify-end gap-2">
                <button onClick={onCancel} className="btn-ghost" data-testid={`${testidPrefix}-cancel`}>
                    Скасувати
                </button>
                <button onClick={onConfirm} className="btn-primary pulse-glow" data-testid={`${testidPrefix}-yes`}>
                    <Check size={14} /> {confirmLabel}
                </button>
            </div>
        </div>
    </div>
);